import Phaser from 'phaser'
import { BlockSprite } from './BlockSprite'
import type { GameState } from '@/game/types'

export interface CellRect { x: number; y: number; w: number; h: number }

export class BoardView {
  private sprites: BlockSprite[] = []
  private size = 0
  private cell = 0
  private originX = 0
  private originY = 0

  constructor(
    private scene: Phaser.Scene,
    private centerX: number,
    private centerY: number,
    private maxBoardPx: number,
  ) {}

  layout(state: GameState) {
    this.size = state.boardSize
    const gap = this.maxBoardPx * 0.012
    this.cell = (this.maxBoardPx - gap * (this.size + 1)) / this.size
    const total = this.cell * this.size + gap * (this.size + 1)
    this.originX = this.centerX - total / 2 + gap + this.cell / 2
    this.originY = this.centerY - total / 2 + gap + this.cell / 2
    // Rebuild sprite pool
    this.sprites.forEach(s => s.destroy())
    this.sprites = state.blocks.map(b => {
      const sp = new BlockSprite(this.scene, this.cell)
      sp.blockId = b.id
      sp.place(
        this.originX + b.x * (this.cell + gap),
        this.originY + b.y * (this.cell + gap),
      )
      sp.setValue(b.value)
      return sp
    })
  }

  render(state: GameState) {
    const byId = new Map(state.blocks.map(b => [b.id, b]))
    const sel = new Set(state.selectedIds)
    for (const sp of this.sprites) {
      const b = byId.get(sp.blockId)
      if (!b) continue
      sp.setValue(b.value)
      sp.setSelected(sel.has(sp.blockId))
    }
  }

  spriteOf(id: number): BlockSprite | undefined {
    return this.sprites.find(s => s.blockId === id)
  }

  /** 모든 블록을 숨김(일시정지/나가기 시 숫자 가리기). 복원은 render(state)로. */
  concealAll() { this.sprites.forEach(s => s.setVisible(false)) }

  private gap() { return this.maxBoardPx * 0.012 }

  cellAt(px: number, py: number): { row: number; col: number } | null {
    const g = this.gap()
    const col = Math.round((px - this.originX) / (this.cell + g))
    const row = Math.round((py - this.originY) / (this.cell + g))
    if (row < 0 || col < 0 || row >= this.size || col >= this.size) return null
    return { row, col }
  }

  // Returns IDs of non-empty blocks that overlap or have their center inside rect (px coords)
  idsInRect(rect: CellRect, state: GameState): number[] {
    const g = this.gap()
    const ids: number[] = []
    for (const b of state.blocks) {
      if (b.value === 0) continue
      const cx = this.originX + b.x * (this.cell + g)
      const cy = this.originY + b.y * (this.cell + g)
      const left = cx - this.cell / 2
      const top = cy - this.cell / 2
      const overlap = !(
        left > rect.x + rect.w ||
        left + this.cell < rect.x ||
        top > rect.y + rect.h ||
        top + this.cell < rect.y
      )
      const centerIn =
        cx >= rect.x && cx <= rect.x + rect.w && cy >= rect.y && cy <= rect.y + rect.h
      if (overlap || centerIn) ids.push(b.id)
    }
    return ids
  }
}
